import styled, { keyframes } from 'styled-components';
import { Button, Card, Select, DatePicker } from 'antd';
import { COLORS } from '../../constants/colors';

// Animation keyframes
const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const PageContainer = styled.div`
  min-height: 100vh;

  // Global animation classes
  .header-animate-in {
    animation: ${slideInFromTop} 0.6s ease-out forwards;
  }

  .header-animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }

  .filters-animate-in {
    animation: ${slideInFromLeft} 0.6s ease-out forwards;
  }

  .filters-animate-out {
    opacity: 0;
    transform: translateX(-20px);
  }

  .stats-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
  }

  .stats-animate-out {
    opacity: 0;
    transform: scale(0.95);
  }

  .charts-animate-in {
    animation: ${slideInFromRight} 0.6s ease-out forwards;
  }

  .charts-animate-out {
    opacity: 0;
    transform: translateX(20px);
  }
  
  .realtime-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
  }

  .realtime-animate-out {
    opacity: 0;
    transform: scale(0.95);
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 20px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid ${COLORS.border};
  
  /* Full-width border extending to edges */
  margin-left: -48px;
  margin-right: -48px;
  padding-left: 48px;
  padding-right: 48px;
  
  @media (max-width: 768px) {
    margin-left: -40px;
    margin-right: -40px;
    padding-left: 40px;
    padding-right: 40px;
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: #1a1a1a;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
`;

export const ActionButton = styled(Button)`
  height: 48px;
  padding: 0 20px;
  font-weight: 500;
  border-radius: 6px;
  box-shadow: none !important;
  
  &.ant-btn-primary {
    background: #1890ff;
    border-color: #1890ff;
    
    &:hover {
      background: #40a9ff;
      border-color: #40a9ff;
    }
    
    &:focus {
      background: #1890ff;
      border-color: #1890ff;
    }
  }
`;

export const FilterContainer = styled.div`
  margin-bottom: 24px;
  padding: 16px 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const StyledSelect = styled(Select)`
  .ant-select-selector {
    height: 48px !important;
    padding: 0 12px !important;
    display: flex !important;
    align-items: center !important;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    
    .ant-select-selection-search {
      input {
        height: 100% !important;
      }
    }
    
    .ant-select-selection-item {
      line-height: 46px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 46px !important;
    }
  }
  
  &:hover .ant-select-selector {
    border-color: #40a9ff;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export const StyledRangePicker = styled(DatePicker.RangePicker)`
  height: 48px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  
  &:hover {
    border-color: #40a9ff;
  }
  
  &:focus,
  &.ant-picker-focused {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  .ant-picker-input > input {
    font-size: 14px;
  }
`;

export const StatsContainer = styled.div`
  margin-bottom: 24px;
`;

export const StatCard = styled(Card)`
  .ant-card-body {
    padding: 24px;
  }
  
  .ant-statistic-title {
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
  }
  
  .ant-statistic-content {
    font-size: 24px;
    font-weight: 600;
  }
  
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const ChartContainer = styled.div`
  .ant-card {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    .ant-card-head {
      border-bottom: 2px solid #f0f0f0;
    }
    
    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }
`;

export const TableContainer = styled.div`
  .ant-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    .ant-card-head {
      border-bottom: 2px solid #f0f0f0;
    }
    
    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
    }
    
    .ant-table {
      .ant-table-container {
        box-shadow: none;
      }
    }
  }
  
  .ant-table-thead > tr > th {
    background: white;
    font-weight: 600;
    color: #1a1a1a;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #fafafa;
  }
`;
